import { DataTable } from "@/components/activity/data-table";
import type { ActivityLog } from "@/lib/types";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MainTitle } from "@/components/main-title";
import { activityQueryOpts } from "@/hooks/use-activity";
import { useAuth } from "@/providers/auth";
import { useSuspenseQuery } from "@tanstack/react-query";
import { isGated } from "@/hooks/gated";
import GatedOverlay from "@/components/ui/gated-overlay";
import { useOrg } from "@/providers/organization";
import { getOrgIdWithFallback } from "@/lib/utils";
import { ActivityMoreInfo } from "@/components/activity/activity-more-info";
import { columns } from "@/components/activity/table-helper";

export const Route = createFileRoute("/$org/activity")({
  component: ActivityPage,
  loader: async ({ context }) => {
    const { getToken, setToken, handleTokenInvalid } = context.auth;
    const orgId = getOrgIdWithFallback(context.org.orgId);

    const gated = await isGated(
      {
        token: getToken() as string,
        setToken,
        orgId: orgId,
      },
      handleTokenInvalid,
    );

    context.queryClient.ensureQueryData(
      activityQueryOpts({
        token: getToken() ?? "",
        setToken,
        orgId: orgId as string,
      }),
    );

    return { gated: gated?.is_gated };
  },
});

function ActivityPage() {
  const { t } = useTranslation();
  const { gated } = Route.useLoaderData();
  const { org } = Route.useParams();
  const { orgId } = useOrg();
  const resolvedOrgId = getOrgIdWithFallback(orgId);
  const { getToken, setToken } = useAuth();
  const { data } = useSuspenseQuery(
    activityQueryOpts({
      token: getToken() ?? "",
      setToken,
      orgId: resolvedOrgId,
    }),
  );

  const [selectedActivity, setSelectedActivity] = useState<ActivityLog | null>(
    null,
  );
  const [sheetOpen, setSheetOpen] = useState(false);

  const handleRowClick = (activity: ActivityLog) => {
    setSelectedActivity(activity);
    setSheetOpen(true);
  };

  if (gated) {
    return <GatedOverlay org={org} />;
  }

  return (
    <div>
      <MainTitle
        title={t("activity.title")}
        description={t("activity.description")}
      />
      <div>
        <DataTable columns={columns} data={data} onRowClick={handleRowClick} />
      </div>
      <ActivityMoreInfo
        activity={selectedActivity}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
      />
    </div>
  );
}
